import React from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Pagination } from 'swiper';
import { ImageOutlined, Cancel, Add } from '@tmap/mmm-style-guide/src/Icon';
import { Badge } from '@tmap/mmm-style-guide/src/Badge';
import { Button } from '@tmap/mmm-style-guide/src/Button';
import { IconButton } from '@tmap/mmm-style-guide/src/IconButton';
import { Typography } from '@tmap/mmm-style-guide/src/Typography';
import { Box } from '@tmap/mmm-style-guide/src/Box';
import { styled } from '@tmap/mmm-style-guide/src/styled';
import { Tooltip } from '@tmap/mmm-style-guide/src/Tooltip';

import ContributionMedia from '../contributionMedia';

import 'swiper/css';
import 'swiper/css/pagination';

const RemoveIcon = styled(Cancel)(({ theme }) => ({
  color: theme.palette.mode === 'light' ? theme.palette.grey[400] : theme.palette.grey[200],
  opacity: 0.9,
  '&:hover': {
    opacity: 1,
  },
}));

const UploadingSkeletonContainer = styled(Box)(({ theme }) => ({
  borderRadius: '8px',
  height: '250px',
  width: '250px',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  position: 'relative',
  background: theme.palette.grey[800],
  overflow: 'hidden',
}));

const UploadingSkeletonProgress = styled(Box, {
  shouldForwardProp: (p) => p !== 'progress',
})(({ theme, progress }) => ({
  height: `${Math.floor(progress * 1000) / 10}%`,
  width: '100%',
  alignSelf: 'flex-end',
  transition: 'height 0.2s linear',
  background: theme.palette.secondary.light,
}));

function UploadingSkeleton(props) {
  const { progress } = props;
  return (
    <UploadingSkeletonContainer>
      <Box position='absolute' textAlign='center'>
        <ImageOutlined sx={{ fontSize: '64px', color: (theme) => theme.palette.grey[100] }} />
        <Typography component='div' variant='caption2' fontWeight={500} sx={{ color: (theme) => theme.palette.grey[100] }}>
          Uploading...
        </Typography>
      </Box>
      <UploadingSkeletonProgress progress={progress} />
    </UploadingSkeletonContainer>
  );
}

const SlideStyles = styled(Box)(({ theme }) => ({
  display: 'contents',
  '& .swiper': {
    paddingBottom: theme.spacing(4),
    '--swiper-pagination-color': theme.palette.secondary.main,
  },
  '& .swiper-slide': {
    width: '250px',
    height: '250px',
    minHeight: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  '.add-media-slide': {
    width: '118px',
  },
  '& .swiper .swiper-pagination-bullets': {
    bottom: '4px',
  },
}));

const PlaceholderButton = styled(Button)(({ theme }) => ({
  '& button': {
    padding: theme.spacing(3),
    minHeight: '50px',
    borderRadius: '8px',
    border: `1px dashed ${theme.palette.grey[400]}`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: theme.palette.text.secondary,
    '& svg': {
      color: theme.palette.text.secondary,
      fontSize: '20px',
    },
    cursor: 'pointer',
    transitionProperty: 'background',
    transitionDuration: `${theme.transitions.duration.standard}ms`,
    transitionTimingFunction: theme.transitions.easing.easeInOut,
  },
}));

function ContributionMediaInput(props) {
  const {
    onAddClick,
    onRemoveMedia,
    uploadProgress,
    media = [],
  } = props;
  const hasMedia = media.length > 0;
  return (
    <SlideStyles>
      {!hasMedia && uploadProgress === null && (
        <PlaceholderButton
          className='empty-media-button'
          onClick={onAddClick}
          fullWidth
          sx={{ backgroundColor: (theme) => theme.palette.grey[50] }}
        >
          <ImageOutlined />
          <Typography variant='caption' marginLeft={1}>Add Photos and Media</Typography>
        </PlaceholderButton>
      )}
      {(hasMedia || uploadProgress) && (
        <Swiper
          slidesPerView='auto'
          spaceBetween={25}
          pagination={{
            clickable: true,
          }}
          centeredSlides
          modules={[Pagination]}
        >
          {media.map((m, i) => (
            <SwiperSlide key={`contribution-img-${i}`}>
              <Badge badgeContent={(
                <IconButton
                  aria-label='delete image'
                  onClick={onRemoveMedia(i)}
                  size='small'
                  sx={{ position: 'absolute', top: 12, right: 12 }}
                >
                  <RemoveIcon />
                </IconButton>
              )}
              >
                <ContributionMedia media={m} mode='preview' />
              </Badge>
            </SwiperSlide>
          ))}
          {uploadProgress !== null && (
            <SwiperSlide>
              <UploadingSkeleton progress={uploadProgress} />
            </SwiperSlide>
          )}
          <SwiperSlide className='add-media-slide'>
            <Tooltip
              title='Attach Media'
              sx={{ ml: 3, mr: 3 }}
            >
              <PlaceholderButton onClick={onAddClick}>
                <Add />
              </PlaceholderButton>
            </Tooltip>
          </SwiperSlide>
        </Swiper>
      )}
    </SlideStyles>
  );
}

export default ContributionMediaInput;
